import { t } from "@web-monorepo/i18n/macro";
import createAction from "@web-monorepo/infra/createAction";
import { getImageSrc } from "@web-monorepo/static-assets";
import { pick, sample } from "es-toolkit";
import { Student } from "src/pods/students";
import { AppMetadata } from "src/pods/toolkitData/components/EditableSetup";
import computeOptimalBaseAvatarURL from "src/utils/computeOptimalBaseAvatarURL";

const icon = getImageSrc("toolkit/src/pods/random/assets/logo.svg");

export type RandomPayload = {
  student: Student;
  students: Student[];
  baseAvatarURL?: string;
};

export type RandomState = {
  student?: Student;
  students?: Student[];
  previouslyChosenStudents: Student[];
};

export const appMetadataRandom = (): AppMetadata => ({
  href: "random",
  title: t`Random`,
  variant: "random",
  icon,
});

export const initialRandomState: RandomState = {
  student: undefined,
  students: undefined,
  previouslyChosenStudents: [],
};

export const RECEIVE_RANDOM = createAction("RECEIVE_RANDOM");

function getStudentKey({ _id, firstName, lastName }: Student) {
  return _id || `${firstName} ${lastName}`;
}

export function chooseRandomStudent(students?: Student[], previouslyChosenStudents?: Student[]) {
  if (!students?.length) throw new Error("`pickRandomStudent` requires students");

  const previouslyChosenStudentKeys = new Set(previouslyChosenStudents?.map(getStudentKey));
  const otherStudents = students?.filter(
    (student: Student) => !previouslyChosenStudentKeys.has(getStudentKey(student)),
  );
  const chosenStudent = otherStudents.length ? sample(otherStudents) : sample(students);

  return chosenStudent;
}

export function displayStudent(students?: Student[], chosenStudent?: Student) {
  return {
    type: RECEIVE_RANDOM,
    payload: deflatePayload(students, chosenStudent),
  };
}

/**
 * This function aims to return only the necessary student data to be sent over the network. We want
 * avoid sending unnecessary data in order to minimize traffic costs.
 *
 * @param  {Object}   chosenStudent
 * @param  {[]Object} students
 * @return {Object}
 */
function deflatePayload(students?: Student[], chosenStudent?: Student) {
  const onlyNecessaryStudentProps: (student: Student) => Partial<Student> = (s) =>
    pick(s, ["_id", "firstName", "lastName", "avatar"]);
  const baseAvatarURL = computeOptimalBaseAvatarURL((students ?? []).map((s) => s.avatar));

  const optimizeAvatarURL = ({ avatar, ...student }: Partial<Student>) => ({
    ...student,
    avatar: avatar?.replace(baseAvatarURL!, ""),
  });

  return {
    student: optimizeAvatarURL(onlyNecessaryStudentProps(chosenStudent!)),
    students: students?.map(onlyNecessaryStudentProps).map(optimizeAvatarURL),
    baseAvatarURL,
  };
}

export function inflateRandomPayload(payload: RandomPayload) {
  let { student, students } = payload;
  const { baseAvatarURL } = payload;
  const inflateStudent = ({ firstName, lastName, avatar, ...rest }: Student) => {
    return {
      ...rest,
      firstName,
      lastName,
      avatar: `${baseAvatarURL || ""}${avatar}`,
    };
  };

  students = students.map(inflateStudent);
  student = inflateStudent(student);
  return { student, students };
}
